import { useCallback, useEffect, useState } from 'react'
import { getOrgSettings, saveOrgInstructions, MAX_INSTRUCTIONS } from '../api/orgSettings'

const PLACEHOLDER = `Sign off as "The Support Team".
Never promise a delivery date.
Mention our 30-day guarantee when a customer asks about returns.`

/**
 * House style for the whole Zoho org, appended to every draft.
 *
 * Only tone and wording belong here. The rules that stop the model stating
 * things absent from the knowledge base are built in and cannot be edited —
 * a customer must not be able to switch off the behaviour that keeps replies
 * truthful.
 *
 * Tones and the sending address used to sit underneath, which made one screen
 * ask for three unrelated decisions. They have screens of their own.
 */
export function InstructionsSection() {
  const [value, setValue] = useState('')
  const [saved, setSaved] = useState('')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)

  const load = useCallback(async () => {
    try {
      const res = await getOrgSettings()
      setValue(res.customInstructions)
      setSaved(res.customInstructions)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function save() {
    setBusy(true)
    setError('')
    setDone(false)
    try {
      const res = await saveOrgInstructions(value)
      setSaved(res.customInstructions)
      setDone(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const dirty = value !== saved
  const over = value.length > MAX_INSTRUCTIONS

  return (
    <div className="card">
      <h2>Reply instructions</h2>
      <p className="hint">Tone and wording for every reply. Shared across your Zoho org.</p>

      {error && <div className="error">{error}</div>}

      <textarea
        value={value}
        onChange={(e) => {
          setValue(e.target.value)
          setDone(false)
        }}
        placeholder={loading ? 'Loading…' : PLACEHOLDER}
        disabled={loading || busy}
        rows={6}
      />

      <p className="note" style={over ? { color: 'var(--err-fg)' } : undefined}>
        {value.length} / {MAX_INSTRUCTIONS}
      </p>

      <button onClick={() => void save()} disabled={loading || busy || !dirty || over}>
        {busy ? 'Saving…' : done && !dirty ? 'Saved' : 'Save instructions'}
      </button>

      <p className="note">
        Accuracy rules are built in and cannot be changed here. Replies will never state
        something that is not in your documents.
      </p>
    </div>
  )
}
